import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, resolveDirective as _resolveDirective, createElementVNode as _createElementVNode, withDirectives as _withDirectives, withCtx as _withCtx, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createBlock as _createBlock, createTextVNode as _createTextVNode, mergeProps as _mergeProps, createSlots as _createSlots } from "vue"

const _hoisted_1 = {
  key: 0,
  "data-testid": "table-views-share-warning"
}
const _hoisted_2 = { key: 2 }
const _hoisted_3 = ["id"]

import { computed, ref, useId } from 'vue';
import { useStore } from 'vuex';

import DetailText from '@shell/components/DetailText.vue';
import RichTranslation from '@shell/components/RichTranslation.vue';
import { RcButton } from '@components/RcButton';
import { RcModal } from '@components/RcModal';
import { useI18n } from '@shell/composables/useI18n';
import { importTableView, sharedTableType } from '@shell/utils/table-views/share';
import type { SharedTableView } from '@shell/utils/table-views/share';

// i18n-uses tableViews.share.*, tableViews.import.*


export default /*@__PURE__*/_defineComponent({
  __name: 'TableViewShareModal',
  props: {
    modal: {},
    mode: {},
    value: { default: '' },
    tableKey: { default: '' },
    intoNewView: { type: Boolean, default: false },
    hasFilter: { type: Boolean, default: false }
  },
  emits: ["import"],
  setup(__props: any, { emit: __emit }) {

/**
 * Share View and Import View: a table's view as text, to copy out or to paste in. Both show it in the
 * same DetailText box, with Copy on one and Paste on the other, laid out as the export modal is
 */
const props = __props;

const emit = __emit;

const store = useStore();
const { t } = useI18n(store);

const fieldId = useId();

const pasted = ref('');

const sharing = computed(() => props.mode === 'share');

const introKey = computed(() => {
  if (sharing.value) {
    return 'tableViews.share.intro';
  }

  return props.intoNewView ? 'tableViews.import.introNew' : 'tableViews.import.intro';
});

const imported = computed(() => (sharing.value ? null : importTableView(pasted.value, props.tableKey)));

/** Said once something is in the box: the text isn't a view, or is one for another table */
const problem = computed(() => {
  const result = imported.value;

  if (!result || !('problem' in result) || !pasted.value.trim()) {
    return '';
  }

  // Raw, as it is shown as text: escaped, an apostrophe would read as &#39;
  if (result.problem === 'invalid') {
    return t('tableViews.import.invalid', undefined, true);
  }

  const type = sharedTableType(result.tableKey);
  const schema = store.getters['cluster/schemaFor']?.(type) || store.getters['management/schemaFor']?.(type) || { id: type };
  const list = store.getters['type-map/labelFor'](schema, 2);

  // The same type kept by another page, eg the clusters on Home and in Cluster Management
  return t(type === sharedTableType(props.tableKey) ? 'tableViews.import.otherPage' : 'tableViews.import.otherTable', { list }, true);
});

const canImport = computed(() => !!imported.value && 'view' in imported.value);

const doImport = () => {
  const result = imported.value;

  if (result && 'view' in result) {
    emit('import', result.view);
    props.modal.onClose();
  }
};

return (_ctx: any,_cache: any) => {
  const _directive_clean_html = _resolveDirective("clean-html")!

  return (_openBlock(), _createBlock(_unref(RcModal), _mergeProps(__props.modal, {
    title: _unref(t)(`tableViews.${ __props.mode }.title`),
    class: "share-modal",
    "data-testid": `table-views-${ __props.mode }-modal`
  }), _createSlots({
    default: _withCtx(() => [
      _createVNode(RichTranslation, {
        k: introKey.value,
        tag: "p",
        class: "share-intro"
      }, {
        b: _withCtx(({ content }) => [
          _withDirectives(_createElementVNode("b", null, null, 512), [
            [_directive_clean_html, content]
          ])
        ]),
        _: 1
      }, 8, ["k"]),
      _cache[2] || (_cache[2] = _createTextVNode()),
      (sharing.value && __props.hasFilter)
        ? (_openBlock(), _createElementBlock("p", _hoisted_1, _toDisplayString(_unref(t)('tableViews.filterWarning', undefined, true)), 1))
        : _createCommentVNode("", true),
      _cache[3] || (_cache[3] = _createTextVNode()),
      (sharing.value)
        ? (_openBlock(), _createBlock(DetailText, {
            key: 1,
            label: _unref(t)('tableViews.share.label'),
            value: __props.value,
            "max-length": Infinity,
            "data-testid": "table-views-share-text"
          }, null, 8, ["label", "value"]))
        : (_openBlock(), _createElementBlock("div", _hoisted_2, [
            _createVNode(DetailText, {
              value: pasted.value,
              "onUpdate:value": _cache[0] || (_cache[0] = ($event: any) => ((pasted).value = $event)),
              label: _unref(t)('tableViews.share.label'),
              copy: false,
              editable: "",
              paste: "",
              placeholder: _unref(t)('tableViews.import.placeholder'),
              invalid: !!problem.value,
              "described-by": problem.value ? `${ _unref(fieldId) }-problem` : undefined,
              "data-testid": "table-views-import-text"
            }, null, 8, ["value", "label", "placeholder", "invalid", "described-by"]),
            _cache[1] || (_cache[1] = _createTextVNode()),
            (problem.value)
              ? (_openBlock(), _createElementBlock("p", {
                  key: 0,
                  id: `${ _unref(fieldId) }-problem`,
                  class: "share-problem text-error",
                  "aria-live": "polite",
                  "data-testid": "table-views-import-problem"
                }, _toDisplayString(problem.value), 9, _hoisted_3))
              : _createCommentVNode("", true)
          ])),
      _cache[4] || (_cache[4] = _createTextVNode()),
      _cache[5] || (_cache[5] = _createTextVNode())
    ]),
    _: 2
  }, [
    (sharing.value)
      ? {
          name: "actions",
          fn: _withCtx(({ close }) => [
            _createVNode(_unref(RcButton), {
              variant: "primary",
              size: "large",
              "data-testid": "table-views-share-close",
              onClick: close
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('generic.close')), 1)
              ]),
              _: 1
            }, 8, ["onClick"])
          ]),
          key: "0"
        }
      : {
          name: "primary-action",
          fn: _withCtx(() => [
            _createVNode(_unref(RcButton), {
              variant: "primary",
              size: "large",
              disabled: !canImport.value,
              "data-testid": "table-views-import-confirm",
              onClick: doImport
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(_unref(t)('tableViews.import.import')), 1)
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          key: "1"
        }
  ]), 1040, ["title", "data-testid"]))
}
}

})